import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, catchError, of } from 'rxjs';

// PrimeNG — already in Carmen's package.json
import { AutoCompleteModule, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { NpiLookupService } from './npi-lookup.service';
import { NpiProvider, NpiSelectionEvent } from './npi-provider.model';

@Component({
  selector: 'app-npi-lookup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AutoCompleteModule,
    InputTextModule,
    ButtonModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './npi-lookup.component.html',
  styleUrl: './npi-lookup.component.scss',
})
export class NpiLookupComponent implements OnInit, OnDestroy {

  /** Optional: constrain results to a state (2-letter code, e.g. 'GA') */
  @Input() stateFilter: string | null = null;

  /** Optional: pre-populate with an existing NPI */
  @Input() initialNpi: string | null = null;

  /** Optional: label shown above the field */
  @Input() label = 'Provider (NPI Lookup)';

  /** Optional: allow manual entry toggle */
  @Input() allowManualEntry = true;

  /** Emitted when a provider is selected (from lookup or manual entry) */
  @Output() providerSelected = new EventEmitter<NpiSelectionEvent>();

  /** Emitted when the selection is cleared */
  @Output() providerCleared = new EventEmitter<void>();

  // ── State ──
  mode: 'search' | 'manual' = 'search';
  suggestions: NpiProvider[] = [];
  selectedProvider: NpiProvider | null = null;
  searchText = '';
  loading = false;

  // Manual entry fields
  manualNpi = '';
  manualName = '';
  manualType = '';
  manualPhone = '';

  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private npiService: NpiLookupService) {}

  ngOnInit(): void {
    // Set up debounced search pipeline
    this.search$.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => {
        this.loading = true;
        const search$ = this.stateFilter
          ? this.npiService.searchConstrained(term, `addr_practice.state:${this.stateFilter}`)
          : this.npiService.search(term);
        return search$.pipe(
          catchError(() => {
            this.loading = false;
            return of([]);
          })
        );
      })
    ).subscribe(results => {
      this.suggestions = results;
      this.loading = false;
    });

    // If an initial NPI was provided, look it up
    if (this.initialNpi) {
      this.npiService.getByNpi(this.initialNpi).pipe(
        takeUntil(this.destroy$)
      ).subscribe(provider => {
        if (provider) {
          this.selectedProvider = provider;
          this.searchText = provider.fullName;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── PrimeNG AutoComplete handler ──
  onSearch(event: AutoCompleteCompleteEvent): void {
    this.search$.next(event.query);
  }

  onSelect(event: any): void {
    // PrimeNG 17 emits { value: NpiProvider }, not the provider directly
    const provider: NpiProvider = event?.value ?? event;
    this.selectedProvider = provider;
    this.providerSelected.emit({ provider, source: 'npi_lookup' });
  }

  onClear(): void {
    this.selectedProvider = null;
    this.searchText = '';
    this.suggestions = [];
    this.providerCleared.emit();
  }

  // ── Mode toggle ──
  setMode(mode: 'search' | 'manual'): void {
    this.mode = mode;
    if (mode === 'manual' && this.selectedProvider) {
      // Pre-fill manual fields from existing selection
      this.manualNpi = this.selectedProvider.npi;
      this.manualName = this.selectedProvider.fullName;
      this.manualType = this.selectedProvider.providerType;
      this.manualPhone = this.selectedProvider.phone ?? '';
    }
  }

  // ── Manual entry submission ──
  submitManualEntry(): void {
    if (!this.manualName.trim()) return;

    const provider: NpiProvider = {
      npi: this.manualNpi.trim(),
      fullName: this.manualName.trim(),
      providerType: this.manualType.trim(),
      practiceAddress: '',
      credential: null,
      firstName: null,
      lastName: null,
      gender: null,
      phone: this.manualPhone.trim() || null,
      fax: null,
      city: null,
      state: this.stateFilter,
      zip: null,
    };

    this.selectedProvider = provider;
    this.providerSelected.emit({ provider, source: 'manual_entry' });
  }

  removeSelection(): void {
    this.selectedProvider = null;
    this.searchText = '';
    this.manualNpi = '';
    this.manualName = '';
    this.manualType = '';
    this.manualPhone = '';
    this.providerCleared.emit();
  }

  /** Display function for PrimeNG autocomplete */
  displayName(provider: NpiProvider): string {
    return provider?.fullName ?? '';
  }
}
